import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../..";
import { domainApi } from "../../../services/domains";
import { CountClassifierMetric } from "../../../config/types";
import { DistrictsCountQuery, domainAnalytics, FacilitiesCountQuery, ProgrammesCountQuery } from "../../../services/analytics/domain.analytics";

interface DomainMetricsState {
  loading: boolean;
  totalRegions: number;
  totalDistricts: number;
  totalFacilities: number;
  totalDivision: number;
  totalProgrammes: number;
  error?: string;

  programmesClassifier: CountClassifierMetric | null;
  districtsClassifier: CountClassifierMetric | null;
  facilitiesClassifier: CountClassifierMetric | null;
}

const initialState: DomainMetricsState = {
  loading: true,
  totalRegions: 0,
  totalDistricts: 0,
  totalFacilities: 0,
  totalDivision: 0,
  totalProgrammes: 0,

  programmesClassifier:{
    loading:true
  },
  districtsClassifier:{
    loading:true
  },
  facilitiesClassifier:{
    loading:true
  }
};

export const domainMetricsSlice = createSlice({
  name: "domainMetrics",
  initialState,
  reducers: {
    startFetch: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    update: (state, action: PayloadAction<Partial<DomainMetricsState>>) => {
      Object.assign(state, action.payload);
    },
    fetchFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    updateProgrammesClassifier: (state, action: PayloadAction<CountClassifierMetric>) => {
      state.programmesClassifier = action.payload;
    },
    updateFacilitiesClassifier: (state, action: PayloadAction<CountClassifierMetric>) => {
      state.facilitiesClassifier = action.payload;
    },

    updateDistrictsClassifier: (state, action: PayloadAction<CountClassifierMetric>) => {
      state.districtsClassifier = action.payload;
    },
  },
});

export const { 
  startFetch, 
  update, 
  fetchFailed,
  updateDistrictsClassifier,
  updateFacilitiesClassifier,
  updateProgrammesClassifier
 } = domainMetricsSlice.actions;

export const fetchDomainMetric = (): AppThunk => async (dispatch) => {
  try {
    dispatch(startFetch());

    const [region, district, division, facility, programme] = await Promise.all([
      domainAnalytics.getRegionCount(),
      domainAnalytics.getDistrictsCount(),
      domainAnalytics.getDivisionCount(),
      domainAnalytics.getFacilitiesCount(),
      domainAnalytics.getProgrammesCount(),
    ]);


    dispatch(
      update({
        totalDistricts: district.totalRecords,
        totalFacilities: facility.totalRecords,
        totalRegions: region.totalRecords,
        totalDivision: division.totalRecords,
        totalProgrammes: programme.totalRecords,
        loading: false,
      })
    );
  } catch (err: any) {
    dispatch(fetchFailed(err.message || "Failed to fetch domain metrics"));
  }
};

export const fetchFacilitiesClassifier =
  (query?: FacilitiesCountQuery): AppThunk =>
  async (dispatch) => {
    try {
      const result = await  domainAnalytics.getFacilitiesCount(query)
      dispatch(
        updateFacilitiesClassifier({
          data: result.data,
          loading: false,
        })
      );
    } catch (err) {}
};

export const fetchDistrictsClassifier =
  (query?: DistrictsCountQuery): AppThunk =>
  async (dispatch) => {
    try {
      const result = await  domainAnalytics.getDistrictsCount(query)
      dispatch(
        updateDistrictsClassifier({
          data: result.data,
          loading: false,
        })
      );
    } catch (err) {}
};

export const fetchProgrammesClassifier =
  (query?: ProgrammesCountQuery): AppThunk =>
  async (dispatch) => {
    try {
      const result = await  domainAnalytics.getProgrammesCount(query)
      dispatch(
        updateProgrammesClassifier({
          data: result.data,
          loading: false,
        })
      );
    } catch (err) {}
};

export default domainMetricsSlice.reducer;
